import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { FaTrash } from "react-icons/fa";
import { ISeguroNegociacaoRetorno } from "@/models/consignado/proposal-billings.model";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import SinistroFormModal, { SinistroFormValues } from "./SinistroFormModal";

// Conteúdo da seção "Sinistro" do redesign (HIS-030 / TAR-061): motivo do
// desligamento, comprovante e o histórico de sinistro com criar / editar /
// excluir. Só apresentação — as rotas ficam no SinistroSection.

type Props = {
  motivo?: string | null;
  comprovanteUrl?: string | null;
  onGerarComprovante: () => void;
  isGeneratingComprovante: boolean;
  sinistros: ISeguroNegociacaoRetorno[];
  onSalvar: (values: SinistroFormValues, sinistro: ISeguroNegociacaoRetorno | null) => Promise<unknown>;
  isSaving: boolean;
  onExcluir: (sinistro: ISeguroNegociacaoRetorno) => void;
  excluindoId: string | null;
};

export default function SinistroVinculo({
  motivo,
  comprovanteUrl,
  onGerarComprovante,
  isGeneratingComprovante,
  sinistros,
  onSalvar,
  isSaving,
  onExcluir,
  excluindoId,
}: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editando, setEditando] = useState<ISeguroNegociacaoRetorno | null>(null);

  const abrirNovo = () => {
    setEditando(null);
    onOpen();
  };

  const abrirEdicao = (sinistro: ISeguroNegociacaoRetorno) => {
    setEditando(sinistro);
    onOpen();
  };

  // Erro já vira toast no SinistroSection; aqui só mantém o modal aberto.
  const handleSubmit = async (values: SinistroFormValues) => {
    try {
      await onSalvar(values, editando);
      onClose();
    } catch {
      return;
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <Flex justify="space-between" align="flex-start" gap={4}>
        <Box>
          <Text fontSize="sm" fontWeight="bold" color="gray.600">
            Motivo do Desligamento
          </Text>
          <Text fontSize="md" color="gray.800">
            {motivo ?? "-"}
          </Text>
        </Box>

        {comprovanteUrl ? (
          <Button
            as="a"
            href={comprovanteUrl}
            target="_blank"
            rel="noopener noreferrer"
            colorScheme="primary"
            size="sm"
          >
            Baixar Comprovante
          </Button>
        ) : (
          <Button
            colorScheme="primary"
            size="sm"
            onClick={onGerarComprovante}
            isLoading={isGeneratingComprovante}
            loadingText="Gerando comprovante..."
          >
            Gerar Comprovante
          </Button>
        )}
      </Flex>

      <Box borderWidth="1px" borderRadius="lg" p={4} borderColor="gray.200">
        <Text fontSize="sm" fontWeight="bold" mb={3}>
          Histórico de Sinistro
        </Text>

        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th py={2} fontSize="xs">Data do Contato</Th>
                <Th py={2} fontSize="xs">Descrição</Th>
                <Th py={2} fontSize="xs">Status</Th>
                <Th py={2} fontSize="xs" textAlign="center">Ações</Th>
              </Tr>
            </Thead>

            <Tbody>
              {sinistros.length ? (
                sinistros.map((sinistro) => (
                  <Tr key={sinistro.id}>
                    <Td py={2} whiteSpace="nowrap" fontSize="xs">
                      {formatDateWithNoHours(sinistro.data_contato)}
                    </Td>
                    <Td py={2} maxW="350px" whiteSpace="normal" wordBreak="break-word" fontSize="xs">
                      {sinistro.descricao}
                    </Td>
                    <Td py={2} fontSize="xs">
                      {sinistro.status}
                    </Td>
                    <Td py={2}>
                      <Flex justifyContent="center" gap={1}>
                        <IconButton
                          aria-label="Editar sinistro"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => abrirEdicao(sinistro)}
                        />
                        <IconButton
                          aria-label="Excluir sinistro"
                          icon={<FaTrash />}
                          size="sm"
                          variant="ghost"
                          onClick={() => onExcluir(sinistro)}
                          isLoading={excluindoId === sinistro.id}
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={4}>
                    <Text fontSize="xs" textAlign="center" color="gray.500">
                      Nenhum sinistro registrado.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <Flex justify="flex-start">
        <Button colorScheme="secondary" size="sm" onClick={abrirNovo}>
          Adicionar novo sinistro
        </Button>
      </Flex>

      <SinistroFormModal
        isOpen={isOpen}
        onClose={onClose}
        sinistro={editando}
        onSubmit={handleSubmit}
        isSaving={isSaving}
      />
    </VStack>
  );
}
